import React from 'react'
import PropTypes from 'prop-types'
import { isEmpty, identity } from 'ramda'
import { isString } from 'utils/types'

import { yup } from 'schemas/validation'
import strings from 'utils/strings'
import { ERROR, WARNING } from 'constants/ui'

import {
  RESET,
  SAVE,
  CLEAR,
  DELETE,
  UNAVAILABLE,
  ACTIVATE,
  CUSTOM,
} from 'constants/ui/editor'

import { Header } from 'components/patterns'
import { TextArea } from 'components/patterns/Inputs'
import { OBSOLETE } from 'utils/constants'
import withStyle, {
  StyledIcon,
  StyledSubheading,
  SaveOverlayContainer,
  ListContent,
  NotesContainer,
  StyledListHeader,
  StyledListItemText,
} from './InfoOverlay.style'

const InfoOverlayStateMapping = {
  [RESET]: ERROR,
  [DELETE]: ERROR,
  [SAVE]: WARNING,
  [CLEAR]: WARNING,
  [UNAVAILABLE]: WARNING,
  [ACTIVATE]: WARNING,
  [OBSOLETE]: ERROR,
  [CUSTOM]: WARNING,
}
const InfoOverlayIcon = {
  [RESET]: 'alert_banner_error_diamond_cross',
  [DELETE]: 'alert_exclamation_triangle_solid',
  [SAVE]: 'alert_exclamation_triangle_solid',
  [OBSOLETE]: 'alert_exclamation_triangle_solid',
  [CLEAR]: 'exclamation-circle',
  [UNAVAILABLE]: 'exclamation-circle',
  [ACTIVATE]: 'exclamation-circle',
  [CUSTOM]: 'exclamation-circle',
}
const InfoOverlayFill = {
  [RESET]: 'errorAccent',
  [DELETE]: 'errorAccent',
  [SAVE]: 'warningAccent',
  [CLEAR]: 'filled',
  [UNAVAILABLE]: 'filled',
  [ACTIVATE]: 'filled',
  [OBSOLETE]: 'errorAccent',
  [CUSTOM]: 'filled',
}

const InfoOverlay = ({
  infoState,
  content,
  className,
  submitting,
  notes,
  setNotes,
}) => {
  const overlayProps = content[infoState]

  const renderIcon = () => (
    <StyledIcon
      icon={InfoOverlayIcon[infoState]}
      fill={InfoOverlayFill[infoState]}
      state={InfoOverlayStateMapping[infoState]}
    />
  )
  const renderAtFullHeight = [ACTIVATE, OBSOLETE].includes(infoState)

  return (
    <div
      className={className}
      style={{ height: renderAtFullHeight ? 'calc(100% - 50px)' : 'auto' }}
    >
      {renderIcon()}
      {infoState === SAVE ? (
        <SaveOverlay
          {...overlayProps}
          submitting={submitting}
          notes={notes}
          setNotes={setNotes}
        />
      ) : (
        <GeneralOverlay {...overlayProps} />
      )}
    </div>
  )
}

InfoOverlay.propTypes = {
  className: PropTypes.string,
  infoState: PropTypes.string,
  content: PropTypes.object,
  notes: PropTypes.string,
  setNotes: PropTypes.func,
  submitting: PropTypes.bool,
}

export default withStyle(InfoOverlay)

const renderListItem = (item, formatListItem) => (
  <li key={formatListItem(item)}>
    <StyledListItemText>{formatListItem(item)}</StyledListItemText>
  </li>
)

const renderList = (header, subHeader, collection, formatListItem) => {
  if (isEmpty(collection)) return null

  return (
    <React.Fragment key={header}>
      <StyledListHeader>{header}</StyledListHeader>
      {subHeader && <StyledListHeader>{subHeader}</StyledListHeader>}
      <ul>{collection.map((item) => renderListItem(item, formatListItem))}</ul>
    </React.Fragment>
  )
}

const GeneralOverlay = ({ heading, subheading, lists, formatListItem }) => (
  <div>
    {heading && <Header type="h3">{heading}</Header>}
    {subheading && (
      <StyledSubheading weight="bold">{subheading}</StyledSubheading>
    )}

    {lists &&
      lists.map((list) =>
        renderList(list.heading, list.subHeader, list.items, formatListItem)
      )}
  </div>
)

GeneralOverlay.defaultProps = {
  formatListItem: identity,
}

GeneralOverlay.propTypes = {
  subheading: PropTypes.string,
  heading: PropTypes.string,
  lists: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      items: PropTypes.array,
    })
  ),
  formatListItem: PropTypes.func,
}

const SaveOverlay = ({
  notes,
  setNotes,
  heading,
  subheading,
  lists,
  submitting,
  formatListItem,
}) => (
  <SaveOverlayContainer>
    <ListContent>
      {heading && <Header type="h3">{heading}</Header>}
      {isString(subheading) ? (
        <StyledSubheading weight="bold">{subheading}</StyledSubheading>
      ) : (
        subheading
      )}

      {lists &&
        lists.map((list) =>
          renderList(list.heading, list.subHeader, list.items, formatListItem)
        )}
    </ListContent>

    <NotesContainer>
      <TextArea
        label={strings.notes}
        name={strings.notes}
        size="large"
        onChange={setNotes}
        value={notes}
        placeholder={strings.notes_required}
        autosize={{ minRows: 8, maxRows: 15 }}
        maxLength={1000}
        rules={yup.string()}
        disabled={submitting}
      />
    </NotesContainer>
  </SaveOverlayContainer>
)

SaveOverlay.defaultProps = {
  formatListItem: identity,
}

SaveOverlay.propTypes = {
  heading: PropTypes.string,
  subheading: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  lists: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      items: PropTypes.array,
    })
  ),
  notes: PropTypes.string,
  setNotes: PropTypes.func,
  submitting: PropTypes.bool,
  formatListItem: PropTypes.func,
}
